import React, { FC, useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocumentProxy } from "pdfjs-dist";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Annotation, Reference } from "@/types/types";
import { ZoomIn, ZoomOut } from "lucide-react";
import CustomReactMarkdown from "./CustomReactMarkdown";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import BrandPane from "./BrandPane";
import NavigationPane from "./NavigationPane";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url
).toString();

interface MLRViewProps {
    pdfUrl: string;
    annotations: Annotation[];
}

const MLRView: FC<MLRViewProps> = ({ pdfUrl, annotations }) => {
    const [numPages, setNumPages] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedAnnotation, setSelectedAnnotation] =
        useState<Annotation | null>(null);
    const [scale, setScale] = useState(0.6);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentReference, setCurrentReference] = useState<Reference | null>(
        null
    );
    const [dialogOpen, setDialogOpen] = useState(false);
    const [pdfLoaded, setPdfLoaded] = useState(false);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([
        "PARTIALLY SUPPORTED",
        "NOT SUPPORTED",
    ]);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [highlightReady, setHighlightReady] = useState(false);
    const [initialScaleSet, setInitialScaleSet] = useState(false);

    const REVIEW_STATUSES = [
        "FULLY SUPPORTED",
        "PARTIALLY SUPPORTED",
        "NOT SUPPORTED",
    ];

    // Refs for scrolling
    const containerRef = useRef<HTMLDivElement>(null);
    const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
    const annotationRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const dialogContainerRef = useRef<HTMLDivElement>(null);
    const dialogPageRefs = useRef<(HTMLDivElement | null)[]>([]);

    // Improved scale calculation
    const calculateIdealScale = (page: any) => {
        if (!containerRef.current) return 0.6;

        // Account for all UI elements (padding, controls, margins)
        const containerWidth = containerRef.current.clientWidth - 80; // More padding buffer
        const containerHeight = containerRef.current.clientHeight - 160; // Account for pagination and top controls

        const pageWidth = page.originalWidth;
        const pageHeight = page.originalHeight;

        const scaleX = containerWidth / pageWidth;
        const scaleY = containerHeight / pageHeight;

        // Use a slightly smaller scale to ensure no scrolling
        const idealScale = Math.min(scaleX, scaleY) * 0.95; // 5% smaller for safety margin

        // Ensure scale stays within reasonable bounds
        const scaledScale = Math.min(Math.max(idealScale, 0.3), 1);
        // const scaledScale = idealScale;
        console.log("scaledScale", scaledScale);
        return scaledScale;
    };

    // Modify onDocumentLoadSuccess to include scale calculation
    const onDocumentLoadSuccess = ({ numPages }: PDFDocumentProxy) => {
        setNumPages(numPages);
        setCurrentPage(1);
        setIsLoading(false);
    };

    // Fix zoom controls
    const zoomIn = () => {
        setScale((prevScale) => Math.min(prevScale + 0.1, 2));
    };

    const zoomOut = () => {
        setScale((prevScale) => Math.max(prevScale - 0.1, 0.3));
    };

    // Modify page load success handler
    const onPageLoadSuccess = (page: any) => {
        if (!initialScaleSet) {
            const newScale = calculateIdealScale(page);
            setScale(newScale);
            setInitialScaleSet(true);
        }
    };

    function onDialogDocumentLoadSuccess({ numPages }: { numPages: number }) {
        dialogPageRefs.current = Array(numPages).fill(null);
        setPdfLoaded(true);
        setHighlightReady(false);
    }

    useEffect(() => {
        if (dialogOpen && currentReference && pdfLoaded) {
            const targetPage = currentReference.page + 1; // Adjust for 1-based index
            const pageElement = document.getElementById(
                `dialog-page-${targetPage}`
            );

            if (pageElement) {
                pageElement.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
                console.log("Scrolling to page:", targetPage);
            }
        }
    }, [dialogOpen, currentReference, pdfLoaded]);

    const handleBoxClick = (e: React.MouseEvent, annotation: Annotation) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedAnnotation(annotation);
    };

    const handleDotClick = (e: React.MouseEvent, annotation: Annotation) => {
        e.preventDefault();
        e.stopPropagation();

        // Set the selected annotation
        setSelectedAnnotation(annotation);

        // Wait for state update and DOM to reflect changes
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const annotationElement = annotationRefs.current[annotation.id];
                if (annotationElement) {
                    annotationElement.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }
            });
        });
    };

    const scrollToAnnotation = (annotation: Annotation) => {
        const pageRef = pageRefs.current[annotation.page - 1];
        if (pageRef && containerRef.current) {
            // First scroll the PDF page into view
            const pageRect = pageRef.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();
            const scrollTop =
                pageRef.offsetTop -
                containerRect.height / 2 +
                pageRect.height / 2;

            containerRef.current.scrollTo({
                top: scrollTop,
                behavior: "smooth",
            });

            // Set the selected annotation
            setSelectedAnnotation(
                selectedAnnotation?.id === annotation.id ? null : annotation
            );

            // Wait for state update and DOM to reflect changes
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const annotationElement =
                        annotationRefs.current[annotation.id];
                    if (annotationElement) {
                        annotationElement.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        });
                    }
                });
            });
        }
    };

    const handleSidebarClick = (annotation: Annotation) => {
        scrollToAnnotation(annotation);
    };

    const handlePageClick = () => {
        // Clear selection when clicking on the page background
        setSelectedAnnotation(null);
    };

    const handlePageChange = (offset: number) => {
        setCurrentPage((prevPage) =>
            Math.min(Math.max(prevPage + offset, 1), numPages || 1)
        );
    };

    const getBadgeColor = (status: string) => {
        switch (status) {
            case "PARTIALLY SUPPORTED":
                return "bg-yellow-100 text-yellow-800";
            case "NOT SUPPORTED":
                return "bg-red-100 text-red-800";
            case "FULLY SUPPORTED":
                return "bg-green-100 text-green-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const getCircleColor = (status: string) => {
        switch (status) {
            case "PARTIALLY SUPPORTED":
                return "bg-yellow-500";
            case "NOT SUPPORTED":
                return "bg-red-500";
            case "FULLY SUPPORTED":
                return "bg-green-500";
            default:
                return "bg-gray-500";
        }
    };

    const handleReferenceClick = (
        reference: Reference,
        e?: React.MouseEvent
    ) => {
        // Prevent event bubbling
        e?.preventDefault();
        e?.stopPropagation();

        setCurrentReference(reference);
        setDialogOpen(true);
    };

    const filteredAnnotations = annotations.filter(
        (annotation) =>
            selectedStatuses.length === 0 ||
            selectedStatuses.includes(annotation.reviewStatus)
    );

    // Add a click outside handler to close the dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest(".filter-dropdown")) {
                setIsFilterOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        // Reset highlight when dialog closes
        if (!dialogOpen) {
            setHighlightReady(false);
            setPdfLoaded(false);
            setCurrentReference(null);
        }
    }, [dialogOpen]);

    useEffect(() => {
        if (dialogOpen && currentReference && pdfLoaded) {
            setHighlightReady(true);
        }
    }, [dialogOpen, currentReference, pdfLoaded]);

    useEffect(() => {
        // Handle scrolling after highlight is ready
        if (highlightReady && currentReference && dialogContainerRef.current) {
            const targetPage = currentReference.page + 1;
            const pageRef = dialogPageRefs.current[targetPage];

            if (pageRef) {
                const scrollOptions = {
                    behavior: "smooth" as const,
                    block: "start" as const,
                };

                pageRef.scrollIntoView(scrollOptions);

                console.log(
                    "Scrolling to page:",
                    targetPage + 1,
                    "Ref exists:",
                    !!pageRef
                );
            }
        }
    }, [highlightReady, currentReference]);

    return (
        <div className="flex h-screen bg-gray-100">
            {/* Brand Pane */}
            <BrandPane />

            {/* Navigation Pane */}
            <NavigationPane />

            {/* Main content - wrap your existing content */}
            <div className="flex-1 overflow-hidden">
                {/* Header */}
                <div className="p-6 flex justify-end bg-white">
                    <img
                        src="/synthio_logo_with_text.png"
                        alt="SynthioLabs Logo"
                        className="h-8 w-auto"
                    />
                </div>
                <div className="flex flex-col w-full min-h-screen bg-gray-100 p-4">
                    <div className="flex w-[95%] h-[calc(100vh-2rem)] bg-white rounded-lg shadow-lg">
                        {/* Left side - PDF Viewer */}
                        <div
                            ref={containerRef}
                            className="flex-[3] h-full border-r border-gray-200 relative flex flex-col"
                        >
                            {/* Zoom controls */}
                            <div className="sticky top-4 z-20 w-full px-4">
                                <div className="flex gap-2 float-right">
                                    <button
                                        onClick={zoomIn}
                                        className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
                                        title="Zoom In"
                                    >
                                        <ZoomIn size={20} />
                                    </button>
                                    <button
                                        onClick={zoomOut}
                                        className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
                                        title="Zoom Out"
                                    >
                                        <ZoomOut size={20} />
                                    </button>
                                </div>
                            </div>

                            {isLoading && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="text-gray-600">
                                        Loading PDF...
                                    </div>
                                </div>
                            )}

                            {/* Main content wrapper - add overflow-y-auto here */}
                            <div className="flex-1 overflow-y-auto min-h-0 relative">
                                <Document
                                    file={pdfUrl}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    loading={<div>Loading PDF...</div>}
                                    className="flex flex-col items-center p-4 w-full"
                                >
                                    <div
                                        key={`page_${currentPage}`}
                                        ref={(el) =>
                                            (pageRefs.current[currentPage - 1] =
                                                el)
                                        }
                                        className="relative mb-4 flex justify-center w-full"
                                        onClick={handlePageClick}
                                    >
                                        <div className="relative w-full flex justify-center">
                                            <div className="relative">
                                                <Page
                                                    pageNumber={currentPage}
                                                    scale={scale}
                                                    className="shadow-md"
                                                    width={Math.min(
                                                        window.innerWidth * 0.6,
                                                        1000
                                                    )}
                                                    onLoadSuccess={
                                                        onPageLoadSuccess
                                                    }
                                                />

                                                {/* Annotations overlay for this page */}
                                                <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                                                    {filteredAnnotations
                                                        .filter(
                                                            (ann) =>
                                                                ann.page ===
                                                                currentPage
                                                        )
                                                        .map(
                                                            (
                                                                annotation,
                                                                index
                                                            ) =>
                                                                annotation.boundingBox ? (
                                                                    <div
                                                                        key={
                                                                            annotation.id
                                                                        }
                                                                        className="absolute pointer-events-auto"
                                                                        style={{
                                                                            left: `${
                                                                                annotation
                                                                                    .boundingBox
                                                                                    .x -
                                                                                annotation
                                                                                    .boundingBox
                                                                                    .width /
                                                                                    2 -
                                                                                0.5
                                                                            }%`,
                                                                            top: `${
                                                                                annotation
                                                                                    .boundingBox
                                                                                    .y -
                                                                                annotation
                                                                                    .boundingBox
                                                                                    .height /
                                                                                    2 -
                                                                                0.5
                                                                            }%`,
                                                                            width: `${
                                                                                annotation
                                                                                    .boundingBox
                                                                                    .width +
                                                                                1
                                                                            }%`,
                                                                            height: `${
                                                                                annotation
                                                                                    .boundingBox
                                                                                    .height +
                                                                                1
                                                                            }%`,
                                                                        }}
                                                                    >
                                                                        {/* Show bounding box when selected */}
                                                                        {selectedAnnotation?.id ===
                                                                            annotation.id && (
                                                                            <div className="absolute inset-0">
                                                                                <span className="relative flex h-full w-full">
                                                                                    <span
                                                                                        className="absolute inline-flex h-full w-full border-2 border-blue-800 bg-blue-500/10 opacity-75 cursor-pointer"
                                                                                        onClick={(
                                                                                            e
                                                                                        ) =>
                                                                                            handleBoxClick(
                                                                                                e,
                                                                                                annotation
                                                                                            )
                                                                                        }
                                                                                    ></span>
                                                                                </span>
                                                                                {/* Add circle in gray when selected */}
                                                                                <div
                                                                                    className="absolute w-6 h-6 flex items-center justify-center text-white font-bold rounded-full cursor-pointer z-10 bg-gray-400"
                                                                                    style={{
                                                                                        top: `${
                                                                                            10 +
                                                                                            index *
                                                                                                5
                                                                                        }%`,
                                                                                        right: "-5%",
                                                                                        transform:
                                                                                            "translate(50%, -50%)",
                                                                                    }}
                                                                                    onClick={(
                                                                                        e
                                                                                    ) =>
                                                                                        handleDotClick(
                                                                                            e,
                                                                                            annotation
                                                                                        )
                                                                                    }
                                                                                    title="Click to view annotation"
                                                                                >
                                                                                    <span className="text-xs">
                                                                                        {Number(
                                                                                            annotation.id
                                                                                        ) +
                                                                                            1}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {/* Show circle only when not selected */}
                                                                        {selectedAnnotation?.id !==
                                                                            annotation.id && (
                                                                            <div
                                                                                className={`absolute w-6 h-6 flex items-center justify-center text-white font-bold rounded-full cursor-pointer z-10 ${getCircleColor(
                                                                                    annotation.reviewStatus
                                                                                )}`}
                                                                                style={{
                                                                                    top: `${
                                                                                        10 +
                                                                                        index *
                                                                                            5
                                                                                    }%`,
                                                                                    right: "-5%",
                                                                                    transform:
                                                                                        "translate(50%, -50%)",
                                                                                }}
                                                                                onClick={(
                                                                                    e
                                                                                ) =>
                                                                                    handleDotClick(
                                                                                        e,
                                                                                        annotation
                                                                                    )
                                                                                }
                                                                                title="Click to view annotation"
                                                                            >
                                                                                <span className="text-xs">
                                                                                    {Number(
                                                                                        annotation.id
                                                                                    ) +
                                                                                        1}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : null
                                                        )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Document>
                            </div>

                            {/* Page navigation controls */}
                            <div className="sticky bottom-0 left-0 right-0 bg-white p-4 border-t border-gray-200 flex justify-between items-center">
                                <button
                                    onClick={() => handlePageChange(-1)}
                                    disabled={currentPage <= 1}
                                    className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100 disabled:opacity-50"
                                >
                                    Previous
                                </button>
                                <span className="text-gray-600">
                                    Page {currentPage} of {numPages}
                                </span>
                                <button
                                    onClick={() => handlePageChange(1)}
                                    disabled={currentPage >= (numPages || 1)}
                                    className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100 disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                        </div>

                        {/* Right side - Annotations Panel */}
                        <div className="flex-1 h-full bg-gray-50 overflow-hidden flex flex-col">
                            <div className="sticky top-0 bg-gray-50 p-4 border-b border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="text-xl font-semibold">
                                        Reviewer Comments
                                    </h2>
                                    <div className="relative filter-dropdown">
                                        <button
                                            onClick={() =>
                                                setIsFilterOpen(!isFilterOpen)
                                            }
                                            className="text-sm border rounded-md px-2 py-1 bg-white flex items-center gap-2"
                                        >
                                            Review Status
                                            <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded-full">
                                                {selectedStatuses.length ||
                                                    "All"}
                                            </span>
                                        </button>

                                        {isFilterOpen && (
                                            <div className="absolute right-0 top-full mt-1 bg-white border rounded-md shadow-lg z-50 min-w-[200px]">
                                                <div className="p-2">
                                                    <label className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer border-b">
                                                        <input
                                                            type="checkbox"
                                                            checked={
                                                                selectedStatuses.length ===
                                                                REVIEW_STATUSES.length
                                                            }
                                                            onChange={(e) => {
                                                                if (
                                                                    e.target
                                                                        .checked
                                                                ) {
                                                                    setSelectedStatuses(
                                                                        [
                                                                            ...REVIEW_STATUSES,
                                                                        ]
                                                                    );
                                                                } else {
                                                                    setSelectedStatuses(
                                                                        []
                                                                    );
                                                                }
                                                            }}
                                                            className="rounded"
                                                        />
                                                        <span className="text-sm font-medium">
                                                            All
                                                        </span>
                                                    </label>
                                                    {REVIEW_STATUSES.map(
                                                        (status) => (
                                                            <label
                                                                key={status}
                                                                className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedStatuses.includes(
                                                                        status
                                                                    )}
                                                                    onChange={(
                                                                        e
                                                                    ) => {
                                                                        if (
                                                                            e
                                                                                .target
                                                                                .checked
                                                                        ) {
                                                                            setSelectedStatuses(
                                                                                [
                                                                                    ...selectedStatuses,
                                                                                    status,
                                                                                ]
                                                                            );
                                                                        } else {
                                                                            setSelectedStatuses(
                                                                                selectedStatuses.filter(
                                                                                    (
                                                                                        s
                                                                                    ) =>
                                                                                        s !==
                                                                                        status
                                                                                )
                                                                            );
                                                                        }
                                                                    }}
                                                                    className="rounded"
                                                                />
                                                                <span className="text-sm">
                                                                    {status}
                                                                </span>
                                                            </label>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4">
                                <div className="space-y-3">
                                    {filteredAnnotations
                                        .filter(
                                            (annotation) =>
                                                annotation.page === currentPage
                                        )
                                        .map((annotation) => (
                                            <div
                                                key={annotation.id}
                                                ref={(el) =>
                                                    (annotationRefs.current[
                                                        annotation.id
                                                    ] = el)
                                                }
                                                className={`p-3 bg-white rounded-lg cursor-pointer transition-all
                                                    hover:bg-gray-50
                                                    ${
                                                        selectedAnnotation?.id ===
                                                        annotation.id
                                                            ? "border-l-4 border-red-500 bg-gray-50"
                                                            : ""
                                                    }`}
                                                onClick={() =>
                                                    handleSidebarClick(
                                                        annotation
                                                    )
                                                }
                                            >
                                                <div className="flex flex-col">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <div className="text-xs text-gray-500">
                                                            Claim{" "}
                                                            {Number(
                                                                annotation.id
                                                            ) + 1}
                                                        </div>
                                                        <span
                                                            className={`inline-block text-xs font-semibold px-2.5 py-0.5 rounded ${getBadgeColor(
                                                                annotation.reviewStatus
                                                            )}`}
                                                        >
                                                            {
                                                                annotation.reviewStatus
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className="text-sm font-medium text-gray-800">
                                                        {annotation.claimText}
                                                    </div>
                                                    {selectedAnnotation?.id ===
                                                        annotation.id && (
                                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                                            <div className="text-sm text-gray-600">
                                                                {annotation.recommendedLanguage && (
                                                                    <div className="mb-2">
                                                                        <strong>
                                                                            Recommended
                                                                            Language:
                                                                        </strong>
                                                                        <div className="italic">
                                                                            <CustomReactMarkdown>
                                                                                {
                                                                                    annotation.recommendedLanguage
                                                                                }
                                                                            </CustomReactMarkdown>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {annotation.rationale && (
                                                                    <div className="mb-2">
                                                                        <strong>
                                                                            Rationale:
                                                                        </strong>
                                                                        <CustomReactMarkdown>
                                                                            {
                                                                                annotation.rationale
                                                                            }
                                                                        </CustomReactMarkdown>
                                                                    </div>
                                                                )}
                                                                {annotation.alternateRecommendedLanguage && (
                                                                    <div className="mb-2">
                                                                        <strong>
                                                                            Alternate
                                                                            Recommended
                                                                            Language:
                                                                        </strong>
                                                                        <div className="italic">
                                                                            <CustomReactMarkdown>
                                                                                {
                                                                                    annotation.alternateRecommendedLanguage
                                                                                }
                                                                            </CustomReactMarkdown>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {annotation.references &&
                                                                    annotation
                                                                        .references
                                                                        .length >
                                                                        0 && (
                                                                        <div className="mb-2">
                                                                            <strong>
                                                                                References:
                                                                            </strong>
                                                                            <div className="space-y-2">
                                                                                {annotation.references.map(
                                                                                    (
                                                                                        reference,
                                                                                        refIndex
                                                                                    ) => (
                                                                                        <blockquote
                                                                                            key={
                                                                                                refIndex
                                                                                            }
                                                                                            onClick={(
                                                                                                e
                                                                                            ) =>
                                                                                                handleReferenceClick(
                                                                                                    reference,
                                                                                                    e
                                                                                                )
                                                                                            }
                                                                                            className="border-l-4 border-gray-200 pl-4 py-2 hover:bg-gray-50 cursor-pointer"
                                                                                        >
                                                                                            {
                                                                                                reference.id
                                                                                            }

                                                                                            :{" "}
                                                                                            {
                                                                                                reference.quote
                                                                                            }
                                                                                        </blockquote>
                                                                                    )
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    {filteredAnnotations.filter(
                                        (annotation) =>
                                            annotation.page === currentPage
                                    ).length === 0 && (
                                        <div className="text-gray-500 text-center py-4">
                                            No annotations available for this
                                            page.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Dialog for Reference Document */}
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogContent className="max-w-3xl h-[80vh] flex flex-col p-0">
                            <DialogHeader className="px-6 py-4">
                                <div className="flex items-center justify-between relative">
                                    <DialogTitle>
                                        Reference Document :{" "}
                                        {currentReference?.docName}
                                    </DialogTitle>
                                    {currentReference && (
                                        <span className="absolute left-1/2 transform -translate-x-1/2 text-sm text-gray-500">
                                            Page {currentReference.page + 1}
                                        </span>
                                    )}
                                    <div className="w-16">
                                        {/* Spacer to balance the layout */}
                                    </div>
                                </div>
                            </DialogHeader>
                            <div
                                className="flex-1 overflow-y-auto min-h-0 relative"
                                ref={dialogContainerRef}
                            >
                                {currentReference && (
                                    <Document
                                        file={currentReference.docName}
                                        onLoadSuccess={
                                            onDialogDocumentLoadSuccess
                                        }
                                        loading={<div>Loading PDF...</div>}
                                        className="flex flex-col items-center p-4 w-full"
                                    >
                                        <div
                                            key={`dialog-page-${
                                                currentReference.page + 1
                                            }`}
                                            id={`dialog-page-${
                                                currentReference.page + 1
                                            }`}
                                            ref={(el) =>
                                                (dialogPageRefs.current[
                                                    currentReference.page + 1
                                                ] = el)
                                            }
                                            className="relative mb-4 flex justify-center w-full"
                                        >
                                            <div className="relative w-full flex justify-center">
                                                <div className="relative">
                                                    <Page
                                                        pageNumber={
                                                            currentReference.page +
                                                            1
                                                        }
                                                        className="shadow-md"
                                                        width={700}
                                                    >
                                                        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                                                            {highlightReady && (
                                                                <div
                                                                    className="absolute inline-flex h-full w-full border-2 border-blue-800 bg-blue-500/10 opacity-75 cursor-pointer"
                                                                    style={{
                                                                        left: `${
                                                                            currentReference
                                                                                .boundingBox
                                                                                .x -
                                                                            currentReference
                                                                                .boundingBox
                                                                                .width /
                                                                                2 -
                                                                            0.5
                                                                        }%`,
                                                                        top: `${
                                                                            currentReference
                                                                                .boundingBox
                                                                                .y -
                                                                            currentReference
                                                                                .boundingBox
                                                                                .height /
                                                                                2 -
                                                                            0.5
                                                                        }%`,
                                                                        width: `${
                                                                            currentReference
                                                                                .boundingBox
                                                                                .width +
                                                                            2
                                                                        }%`,
                                                                        height: `${
                                                                            currentReference
                                                                                .boundingBox
                                                                                .height +
                                                                            2
                                                                        }%`,
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                    </Page>
                                                </div>
                                            </div>
                                        </div>
                                    </Document>
                                )}
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default MLRView;
